import * as React from "react";
import { X, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Toast, ToastVariant } from "./use-toast";

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const variantStyles: Record<ToastVariant, string> = {
  default: "bg-white border-border text-foreground",
  destructive: "bg-destructive/10 border-destructive/30 text-destructive",
  success: "bg-ds-light/30 border-ds-light text-ds-green",
};

const variantIcons: Record<ToastVariant, React.ReactNode> = {
  default: <Info className="w-4 h-4 text-ds-green flex-shrink-0" />,
  destructive: <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />,
  success: <CheckCircle2 className="w-4 h-4 text-ds-accent flex-shrink-0" />,
};

export function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const variant = toast.variant ?? "default";

  return (
    <div
      className={cn(
        "flex items-start gap-3 w-full max-w-sm rounded-lg border p-4 shadow-lg animate-fade-in",
        variantStyles[variant]
      )}
      role="alert"
    >
      {variantIcons[variant]}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="text-sm font-semibold leading-tight">{toast.title}</p>
        )}
        {toast.description && (
          <p className="text-sm mt-0.5 opacity-80">{toast.description}</p>
        )}
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
